// NewNoteView — AI 노트 «새 노트» 화면.
// 제목·폴더·참석자·인식 언어·화자분리를 먼저 정하고, 같은 화면에서 녹음을 시작하거나
// 오디오 파일을 첨부(끌어다 놓기 포함)한다. 메모는 여기서 받지 않는다 — 녹음 시각이 붙는
// 메모는 녹음 화면(RecordingView)에 이미 있다.
import { useEffect, useRef, useState } from 'react';
import { alertDialog } from '../../components/AppDialog';
import { listAudioInputs, pcAudioSupported, readPcAudioPref, writePcAudioPref } from '../../lib/recordingPcAudio';
import { readRawAudioPref, writeRawAudioPref } from '../../lib/recordingPrefs';
import { MEETING_LANGUAGE_OPTIONS, MEETING_THEME, smBtn, softWrap } from '../../utils/meetingUtil';
import { canWriteFolder, flattenTree, indentedName, pathText } from '../../utils/meetingFolderTree';

const T = MEETING_THEME;

// 🔴 값 규칙은 **RecordingContext `cleanRecParticipants`·백엔드 `_participants_clean` 과 짝이다.**
//    한쪽을 바꾸면 셋 다 바꾼다. (서버는 규칙 밖 값을 자르지 않고 400 으로 막는다.)
const PARTICIPANT_NAME_MAX = 40;   // 서버 PARTICIPANT_NAME_MAX
const PARTICIPANTS_MAX = 30;       // 서버 PARTICIPANTS_MAX
const TITLE_MAX = 120;             // RecordingView 제목 input maxLength 와 같게
// 서버 `_AUDIO_EXT` 가 받는 비(非) audio/* MIME.
const EXTRA_AUDIO_TYPES = ['video/webm', 'video/mp4'];
const FILE_ACCEPT = 'audio/*,video/webm,video/mp4';

// 🔴 type 이 빈 문자열이면(일부 브라우저·드래그) 확장자로 추측하지 않고 그대로 서버에 맡긴다 —
//    서버가 `_ALLOWED_AUDIO_EXT` 로 최종 판정하고 안 되면 400 으로 알려 준다.
function isAudioFile(file) {
  const type = String(file?.type || '');
  if (!type) return true;
  return type.startsWith('audio/') || EXTRA_AUDIO_TYPES.includes(type);
}

// «PC 소리도 함께 녹음» select 값 — 'share' 또는 'device:<id>'(저장값과 같은 모양) · '' = 아직 안 고름.
const PC_DEVICE_PREFIX = 'device:';
const PC_SHARE_LABEL = '화면 공유로 받기 (녹음 시작 때 전체 화면·탭을 고르고 오디오 공유 체크)';
const PC_AUDIO_HINT = '회의 프로그램·영상 등 이 PC에서 나는 소리를 마이크와 함께 녹음합니다. 스피커로 들으면 같은 소리가 마이크로도 들어가 울릴 수 있으니 이어폰을 권합니다. "스테레오 믹스" 같은 장치는 Windows 소리 설정에서 켜져 있어야 목록에 나옵니다. 이 브라우저에 기억됩니다.';

function initialPcChoice() {
  const pref = readPcAudioPref();
  if (pref.mode === 'share') return 'share';
  if (pref.mode === 'device') return PC_DEVICE_PREFIX + pref.deviceId;
  return '';
}

// trim → 40자(코드포인트) 자르기 → 다시 trim. cleanRecParticipants 와 같은 순서.
function cleanName(value) {
  return Array.from(String(value ?? '').trim()).slice(0, PARTICIPANT_NAME_MAX).join('').trim();
}

export default function NewNoteView({
  mobile,
  folders,
  sel,
  diarizeDefault,
  disabled,
  uploadPct,
  onStartRecording,
  onUploadFile,
  onClose,
}) {
  // 이동 모달과 같은 기준 — 쓸 수 있는 폴더만 고른다. 순서는 폴더 트리 순서(1단계 → 그 하위).
  const allFolders = Array.isArray(folders) ? folders : [];
  const folderItems = flattenTree(allFolders).filter((it) => canWriteFolder(it.folder));
  const folderList = folderItems.map((it) => it.folder);
  const [title, setTitle] = useState('');
  const [folderId, setFolderId] = useState(() => (
    // 보던 폴더를 기본값으로(그 폴더가 쓸 수 있는 목록에 있을 때만) · 아니면 미분류
    sel?.id != null && folderList.some((f) => String(f.id) === String(sel.id)) ? String(sel.id) : ''
  ));
  const [participants, setParticipants] = useState([]);
  const [nameInput, setNameInput] = useState('');
  const [language, setLanguage] = useState('ko');
  const [diarize, setDiarize] = useState(!!diarizeDefault);
  // «주변 소리 그대로 녹음» — meta 가 아니라 브라우저 선호값이다(startRec 가 직접 읽는다).
  const [rawAudio, setRawAudio] = useState(() => readRawAudioPref());
  // «PC 소리도 함께 녹음» — 이것도 브라우저 선호값이다(startRec 가 직접 읽는다).
  // 🔴 휴대폰(mobile prop)이거나 화면 공유·입력 장치 둘 다 못 쓰는 브라우저면 블록을 그리지 않는다.
  const pcSupport = pcAudioSupported();
  const showPcAudio = !mobile && (pcSupport.share || pcSupport.device);
  const [pcOn, setPcOn] = useState(() => readPcAudioPref().mode !== 'off');
  const [pcChoice, setPcChoice] = useState(() => initialPcChoice());
  const [pcDevices, setPcDevices] = useState([]);
  const mountedRef = useRef(true);
  const fileRef = useRef(null);
  const full = participants.length >= PARTICIPANTS_MAX;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // 입력 장치 목록 — 켜져 있고 이 브라우저가 장치 목록을 줄 때만 읽는다(언마운트 뒤에는 setState 하지 않는다).
  const pcListDevices = showPcAudio && pcOn && pcSupport.device;
  useEffect(() => {
    if (!pcListDevices) return undefined;
    let alive = true;
    listAudioInputs().then((list) => { if (alive) setPcDevices(list); });
    return () => { alive = false; };
  }, [pcListDevices]);

  const togglePcAudio = (checked) => {
    setPcOn(checked);
    if (!checked) { writePcAudioPref('off'); return; }
    if (pcSupport.share) {
      setPcChoice('share');
      writePcAudioPref('share');
    } else {
      setPcChoice('');   // 저장하지 않는다 — 아래 select 에서 장치를 고를 때 저장한다(그동안은 마이크만)
    }
  };
  const choosePcAudio = (value) => {
    setPcChoice(value);
    if (value === 'share') writePcAudioPref('share');
    else if (value.startsWith(PC_DEVICE_PREFIX) && value.length > PC_DEVICE_PREFIX.length) writePcAudioPref('device', value.slice(PC_DEVICE_PREFIX.length));
    else writePcAudioPref('off');
  };
  // 권한을 받기 전에는 장치 이름(label)이 비어 있다 — 마이크 권한을 한 번 받고 곧바로 놓은 뒤 다시 읽는다.
  const revealPcDevices = async () => {
    try {
      const probe = await navigator.mediaDevices.getUserMedia({ audio: true });
      try { probe?.getTracks?.().forEach((t) => t.stop()); } catch {}
    } catch (e) {
      alertDialog('장치 목록을 보려면 마이크 권한이 필요합니다: ' + (e?.message || '권한 거부'));
      return;
    }
    const list = await listAudioInputs();
    if (mountedRef.current) setPcDevices(list);
  };
  const pcDeviceMissing = pcChoice.startsWith(PC_DEVICE_PREFIX)
    && !pcDevices.some((d) => PC_DEVICE_PREFIX + d.deviceId === pcChoice);
  const pcNeedsReveal = pcSupport.device && (pcDevices.length === 0 || pcDevices.every((d) => !d.label));

  const addParticipant = (raw) => {
    const name = cleanName(raw);
    setNameInput('');
    if (!name) return;
    setParticipants((list) => {
      if (list.length >= PARTICIPANTS_MAX) return list;
      if (list.some((p) => p.toLowerCase() === name.toLowerCase())) return list;   // 대소문자 무시 중복
      return [...list, name];
    });
  };
  const removeParticipant = (name) => setParticipants((list) => list.filter((p) => p !== name));

  const pickedFolder = folderList.find((f) => String(f.id) === String(folderId));
  const buildMeta = () => ({
    folderId: folderId === '' ? null : pickedFolder?.id ?? null,
    folderName: pickedFolder?.name || '',
    diarize,
    title: title.trim(),
    participants,
    language,
  });

  const startRecording = () => {
    if (disabled) return;
    onStartRecording?.(buildMeta());
  };
  const takeFile = (file) => {
    if (disabled || !file) return;
    if (!isAudioFile(file)) { alertDialog('오디오 파일만 올릴 수 있습니다.'); return; }
    onUploadFile?.(file, buildMeta());
  };
  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';   // 실패 뒤 같은 파일을 다시 고를 수 있게
    takeFile(f);
  };
  const onDrop = (e) => {
    e.preventDefault();
    takeFile(e.dataTransfer?.files?.[0]);
  };

  const fieldLabel = { display: 'block', fontSize: T.font.sm, fontWeight: 700, color: T.color.slate, marginBottom: '6px' };
  const fieldBox = {
    width: '100%',
    boxSizing: 'border-box',
    minHeight: '38px',
    padding: '8px 10px',
    border: `1px solid ${T.color.border}`,
    borderRadius: T.radius.md,
    background: T.color.surface,
    color: T.color.text,
    fontSize: T.font.md,
    outline: 'none',
  };
  const hint = { marginTop: '6px', fontSize: T.font.xs, color: T.color.faint, ...softWrap };

  return (
    <section style={{ flex: 1, minWidth: 0, overflowY: 'auto', background: T.color.page }}>
      <div style={{ maxWidth: '720px', margin: mobile ? 0 : '0 auto', padding: mobile ? '18px 16px 40px' : '28px 32px 48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <h2 style={{ flex: 1, minWidth: 0, margin: 0, fontSize: mobile ? '22px' : '26px', fontWeight: 800, color: T.color.textStrong, ...softWrap }}>새 노트</h2>
          <button
            type="button"
            onClick={onClose}
            style={{ flexShrink: 0, background: '#f3f4f6', color: '#374151', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, padding: '7px 12px', cursor: 'pointer', fontSize: T.font.base, fontWeight: 600 }}
          >
            ← 목록
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px', background: T.color.surface, border: `1px solid ${T.color.border}`, borderRadius: T.radius.lg, padding: mobile ? '16px' : '20px', boxShadow: T.shadow.card }}>
          {/* 제목 — 🔴 placeholder 를 '새 노트' 로 쓰지 않는다(녹음 화면 제목 input 셀렉터와 겹친다) */}
          <div>
            <label htmlFor="gw-newnote-title" style={fieldLabel}>제목</label>
            <input
              id="gw-newnote-title"
              aria-label="노트 제목"
              placeholder="제목 없음"
              maxLength={TITLE_MAX}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              style={fieldBox}
            />
          </div>

          {/* 폴더 */}
          <div>
            <label htmlFor="gw-newnote-folder" style={fieldLabel}>폴더</label>
            <select
              id="gw-newnote-folder"
              aria-label="폴더"
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              style={{ ...fieldBox, cursor: 'pointer' }}
            >
              <option value="">미분류</option>
              {/* 고른 항목은 전체 경로(«A > B»)로, 나머지는 NBSP 두 칸 × (단계-1) 들여쓰기 + 이름.
                  value·buildMeta 는 그대로 — 폴더 id 만 바뀐다. */}
              {folderItems.map((it) => (
                <option key={it.folder.id} value={String(it.folder.id)}>
                  {String(it.folder.id) === String(folderId)
                    ? (pathText(allFolders, it.folder.id) || it.folder.name)
                    : indentedName(it.folder.name, it.depth)}
                </option>
              ))}
            </select>
          </div>

          {/* 참석자 — Enter·쉼표로 칩 추가 */}
          <div>
            <label htmlFor="gw-newnote-participant" style={fieldLabel}>참석자</label>
            {participants.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                {participants.map((name) => (
                  <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', maxWidth: '100%', padding: '4px 6px 4px 10px', borderRadius: T.radius.pill, border: `1px solid ${T.color.border}`, background: T.color.subtle, color: T.color.text, fontSize: T.font.sm, fontWeight: 700 }}>
                    <span style={{ minWidth: 0, ...softWrap }}>{name}</span>
                    <button
                      type="button"
                      onClick={() => removeParticipant(name)}
                      title={`${name} 삭제`}
                      aria-label={`${name} 삭제`}
                      style={{ width: '18px', height: '18px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, border: 'none', borderRadius: '50%', background: 'transparent', color: T.color.faint, cursor: 'pointer', fontSize: '14px', lineHeight: 1 }}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
            <input
              id="gw-newnote-participant"
              aria-label="참석자 추가"
              placeholder="이름 입력 후 Enter"
              maxLength={PARTICIPANT_NAME_MAX}
              disabled={full}
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  // 🔴 state 가 아니라 input 의 현재 값을 읽는다 — 한글 IME 조합 중에는 state 가 늦다.
                  addParticipant(e.currentTarget.value);
                }
              }}
              style={{ ...fieldBox, opacity: full ? 0.6 : 1 }}
            />
            <div style={hint}>
              {full ? `참석자는 ${PARTICIPANTS_MAX}명까지 넣을 수 있습니다.` : `이름 ${PARTICIPANT_NAME_MAX}자 · 최대 ${PARTICIPANTS_MAX}명`}
            </div>
          </div>

          {/* 인식 언어 — STT 워커가 노트별 언어로 받아쓴다(목록 정본 utils/meetingUtil MEETING_LANGUAGE_OPTIONS). */}
          <div>
            <label htmlFor="gw-newnote-language" style={fieldLabel}>인식 언어</label>
            <select
              id="gw-newnote-language"
              aria-label="인식 언어"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={{ ...fieldBox, cursor: 'pointer' }}
            >
              {MEETING_LANGUAGE_OPTIONS.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </div>

          {/* 화자분리 — 🔴 접근 이름을 '화자분리' 정확 일치로 두지 않는다(기존 사이드바 체크박스와 구별) */}
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '34px', alignSelf: 'flex-start', padding: '0 10px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.subtle, color: T.color.text, fontSize: T.font.sm, fontWeight: 700, cursor: 'pointer' }}>
            <input
              type="checkbox"
              aria-label="화자분리 사용"
              checked={diarize}
              onChange={(e) => setDiarize(e.target.checked)}
              style={{ margin: 0, flexShrink: 0, accentColor: T.color.green }}
            />
            <span style={{ minWidth: 0 }}>화자분리 사용</span>
          </label>

          {/* 주변 소리 그대로 녹음 — 브라우저 음성 처리(잡음 억제 등)가 TV·스피커 소리를 지울 때.
              🔴 buildMeta 에 넣지 않는다 — 값은 이 브라우저 localStorage 에만 있고 startRec 가 읽는다. */}
          <div>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '34px', alignSelf: 'flex-start', padding: '0 10px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.subtle, color: T.color.text, fontSize: T.font.sm, fontWeight: 700, cursor: 'pointer' }}>
              <input
                type="checkbox"
                aria-label="주변 소리 그대로 녹음"
                checked={rawAudio}
                onChange={(e) => { const v = e.target.checked; setRawAudio(v); writeRawAudioPref(v); }}
                style={{ margin: 0, flexShrink: 0, accentColor: T.color.green }}
              />
              <span style={{ minWidth: 0 }}>주변 소리 그대로 녹음</span>
            </label>
            <div style={hint}>브라우저의 잡음 억제·에코 제거·자동 음량을 끕니다. TV·스피커 소리가 녹음되지 않을 때 켜 보세요. 대신 주변 잡음도 그대로 들어갑니다. 이 브라우저에 기억됩니다.</div>
          </div>

          {/* PC 소리도 함께 녹음 — 화면 공유 오디오 또는 고른 입력 장치(스테레오 믹스 등)를 마이크와 섞는다.
              🔴 buildMeta 에 넣지 않는다 — 값은 이 브라우저 localStorage 에만 있고 startRec 가 읽는다. */}
          {showPcAudio && (
            <div>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', minHeight: '34px', alignSelf: 'flex-start', padding: '0 10px', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, background: T.color.subtle, color: T.color.text, fontSize: T.font.sm, fontWeight: 700, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  aria-label="PC 소리도 함께 녹음"
                  checked={pcOn}
                  onChange={(e) => togglePcAudio(e.target.checked)}
                  style={{ margin: 0, flexShrink: 0, accentColor: T.color.green }}
                />
                <span style={{ minWidth: 0 }}>PC 소리도 함께 녹음</span>
              </label>
              {pcOn && (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <select
                    aria-label="PC 소리 받는 방법"
                    value={pcChoice}
                    onChange={(e) => choosePcAudio(e.target.value)}
                    style={{ ...fieldBox, flex: '1 1 260px', width: 'auto', minWidth: 0, cursor: 'pointer' }}
                  >
                    {pcChoice === '' && <option value="">장치를 고르세요</option>}
                    {pcSupport.share && <option value="share">{PC_SHARE_LABEL}</option>}
                    {pcDevices.map((d) => (
                      <option key={d.deviceId} value={PC_DEVICE_PREFIX + d.deviceId}>{`장치: ${d.label || '이름 없는 장치'}`}</option>
                    ))}
                    {pcDeviceMissing && <option value={pcChoice}>장치: (연결 안 됨)</option>}
                  </select>
                  {pcNeedsReveal && (
                    <button
                      type="button"
                      onClick={revealPcDevices}
                      style={{ flexShrink: 0, background: '#f3f4f6', color: '#374151', border: `1px solid ${T.color.border}`, borderRadius: T.radius.md, padding: '7px 12px', cursor: 'pointer', fontSize: T.font.sm, fontWeight: 600 }}
                    >
                      장치 목록 보기
                    </button>
                  )}
                </div>
              )}
              <div style={hint}>{PC_AUDIO_HINT}</div>
            </div>
          )}

          <div style={{ borderTop: `1px solid ${T.color.borderSoft}`, paddingTop: '16px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            <button
              type="button"
              onClick={startRecording}
              disabled={disabled}
              style={{ ...smBtn(T.color.green), padding: '10px 18px', fontSize: T.font.md, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
            >
              녹음 시작
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={disabled}
              style={{ ...smBtn(T.color.slate), padding: '10px 18px', fontSize: T.font.md, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1 }}
            >
              파일 선택
            </button>
            <input
              ref={fileRef}
              type="file"
              aria-label="오디오 파일 선택"
              accept={FILE_ACCEPT}
              onChange={onFilePick}
              style={{ display: 'none' }}
            />
          </div>

          {/* 업로드 진행률 — 업로드가 끝나야 이 화면이 닫히므로(MeetingsPage onUploadFile),
              모바일에서는 이것이 유일한 진행 표시다(사이드바 막대는 데스크톱 전용). */}
          {uploadPct != null && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: T.font.xs, color: T.color.muted, marginBottom: '4px' }}>
                <span>업로드 중…</span><span>{uploadPct}%</span>
              </div>
              <div style={{ height: '5px', background: T.color.borderSoft, borderRadius: T.radius.pill, overflow: 'hidden' }}>
                <div style={{ width: `${uploadPct}%`, height: '100%', background: T.color.green, transition: 'width .2s' }} />
              </div>
            </div>
          )}

          {/* 끌어다 놓기 — role='button' 로 두지 않는다(누르는 자리는 위 [파일 선택]) */}
          <div
            data-testid="newnote-dropzone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            style={{ border: `1px dashed ${T.color.circleBorder}`, borderRadius: T.radius.md, background: T.color.subtle, color: T.color.muted, padding: mobile ? '18px 12px' : '24px 16px', textAlign: 'center', fontSize: T.font.base, fontWeight: 600, opacity: disabled ? 0.6 : 1 }}
          >
            오디오 파일을 여기로 끌어다 놓으세요
          </div>
        </div>
      </div>
    </section>
  );
}
